import React, { useEffect, useRef, useState } from 'react';
import { blogData } from '../data/mock';
import { ArrowUpRight, Clock, BookOpen } from 'lucide-react';
import { Badge } from './ui/badge';

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

export default function BlogSection() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);

  return (
    <section id="blog" className="relative" ref={sectionRef}>
      <div className="section-container">
        <div className={`fade-in-up ${visible ? 'visible' : ''}`}>
          <p className="section-label">// Insights & Analysis</p>
          <h2 className="section-title">Latest Writings</h2>
          <div className="glow-line w-20 mb-10" />
        </div>

        <div className="space-y-4">
          {blogData.map((post, idx) => (
            <a
              key={post.id}
              href={post.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`block cyber-card p-6 group fade-in-up ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.1 + idx * 0.12}s` }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Left - Icon */}
                <div className="w-12 h-12 rounded-lg bg-[#00e5ff]/8 border border-[#00e5ff]/20 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-[#00e5ff]" />
                </div>

                {/* Center - Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <Badge className="bg-[#00e5ff]/8 text-[#00e5ff] border-[#00e5ff]/20 text-[10px] font-['JetBrains_Mono'] hover:bg-[#00e5ff]/15">
                      {post.category}
                    </Badge>
                    <span className="flex items-center gap-1 text-[10px] text-[#64748b] font-['JetBrains_Mono']">
                      <Clock className="w-3 h-3" /> {post.readTime}
                    </span>
                    <span className="text-[10px] text-[#64748b] font-['JetBrains_Mono']">{post.date}</span>
                  </div>
                  <h3 className="text-base font-semibold text-[#f1f5f9] group-hover:text-[#00e5ff] transition-colors duration-300 mb-1.5 truncate">
                    {post.title}
                  </h3>
                  <p className="text-sm text-[#94a3b8] leading-relaxed line-clamp-1">
                    {post.excerpt}
                  </p>
                </div>

                {/* Right - Arrow */}
                <div className="w-10 h-10 rounded-full border border-[#1e293b] flex items-center justify-center flex-shrink-0 group-hover:border-[#00e5ff]/40 group-hover:bg-[#00e5ff]/5 transition-colors duration-300">
                  <ArrowUpRight className="w-4 h-4 text-[#64748b] group-hover:text-[#00e5ff] transition-colors duration-300" />
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className={`mt-8 text-center fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.5s' }}>
          <a
            href="https://saraswatirepository.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 btn-cyber rounded-md"
          >
            Visit Saraswati Repository <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
